import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  InputAdornment,
  Autocomplete,
  Typography,
  Box
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useNotification } from '../layouts/Layout';
import subcontractorService, { Subcontractor, CreateSubcontractorDataWithFile } from '../../services/subcontractorService';
import axios from 'axios';

interface AddSubcontractorFormProps {
  open: boolean;
  onClose: () => void;
  onAdd: (subcontractor: Subcontractor) => void;
  projectId: string;
}

const AddSubcontractorForm: React.FC<AddSubcontractorFormProps> = ({
  open,
  onClose,
  onAdd,
  projectId
}) => {
  const [formData, setFormData] = useState<CreateSubcontractorDataWithFile>({
    contract_number: '',
    name: '',
    email: '',
    phone: '',
    contract_subject: '',
    contract_value: 0,
    completion_date: '',
    signed_date: '',
    project_id: projectId,
    contract_file: null as unknown as File
  });
  
  const [contractDrafts, setContractDrafts] = useState<{id: string, number: string, contractor: string}[]>([]);
  const [selectedDraft, setSelectedDraft] = useState<string | null>(null);
  const [contractFile, setContractFile] = useState<File | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { showNotification } = useNotification();

  // Pobieranie draftów umów
  const fetchContractDrafts = useCallback(async () => {
    try {
      setLoading(true);
      // W rzeczywistej implementacji, to będzie pobierane z API
      const mockDrafts = [
        { id: '1', number: 'DRAFT-2025-001', contractor: 'Budimex S.A.' },
        { id: '2', number: 'DRAFT-2025-002', contractor: 'Elektromont Sp. z o.o.' },
        { id: '3', number: 'DRAFT-2025-003', contractor: 'Instalex S.A.' }
      ];
      setContractDrafts(mockDrafts);
    } catch (error) {
      console.error('Błąd podczas pobierania draftów umów:', error);
      showNotification('Nie udało się pobrać draftów umów', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // Obsługa wyboru draftu
  const handleDraftSelect = (event: React.SyntheticEvent, value: {id: string, number: string, contractor: string} | null) => {
    if (value) {
      setSelectedDraft(value.id);
      setFormData(prev => ({
        ...prev,
        contract_number: value.number,
        name: value.contractor,
        // Inne pola mogą być również wypełnione na podstawie draftu
      }));
    } else {
      setSelectedDraft(null);
    }
  };

  // Obsługa zmiany daty terminu wykonania
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Usunięcie błędu po wprowadzeniu wartości
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Obsługa przesyłania pliku
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setContractFile(file);
      setFormData(prev => ({
        ...prev,
        contract_file: file
      }));
    }
  };

  // Resetowanie formularza przy otwarciu
  useEffect(() => {
    if (open) {
      fetchContractDrafts();
      setFormData({
        contract_number: '',
        name: '',
        email: '',
        phone: '',
        contract_subject: '',
        contract_value: 0,
        completion_date: '',
        signed_date: '',
        project_id: projectId,
        contract_file: null as unknown as File
      });
      setSelectedDraft(null);
      setContractFile(null);
      setErrors({});
    }
  }, [open, projectId, fetchContractDrafts]);

  // Obsługa zmiany pól formularza
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Usunięcie błędu po wprowadzeniu wartości
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Obsługa zmiany pól numerycznych
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const numValue = value === '' ? 0 : parseFloat(value);
    
    if (!isNaN(numValue)) {
      setFormData(prev => ({ ...prev, [name]: numValue }));
      
      // Usunięcie błędu po wprowadzeniu wartości
      if (errors[name]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  // Walidacja formularza
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.completion_date) {
      newErrors.completion_date = 'Termin wykonania jest wymagany';
    }
    
    if (!formData.contract_number.trim()) {
      newErrors.contract_number = 'Numer umowy jest wymagany';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nazwa podwykonawcy jest wymagana';
    }
    
    if (!formData.contract_subject.trim()) {
      newErrors.contract_subject = 'Przedmiot umowy jest wymagany';
    }
    
    if (formData.contract_value <= 0) {
      newErrors.contract_value = 'Wartość umowy musi być większa od 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Obsługa dodawania podwykonawcy
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Przygotowanie danych do wysłania
      let dataToSend;
      
      // Jeśli wybrano plik, wysyłamy dane z plikiem
      if (contractFile) {
        dataToSend = { ...formData };
      } else {
        // Jeśli nie wybrano pliku, tworzymy nowy obiekt bez pola contract_file
        const { contract_file, ...dataWithoutFile } = formData;
        dataToSend = dataWithoutFile;
      }
      
      const newSubcontractor = await subcontractorService.createSubcontractor(dataToSend);
      showNotification('Podwykonawca został dodany pomyślnie', 'success');
      onAdd(newSubcontractor);
      onClose();
    } catch (error) {
      console.error('Błąd podczas dodawania podwykonawcy:', error);
      showNotification('Wystąpił błąd podczas dodawania podwykonawcy', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Dodaj nowego podwykonawcę</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Autocomplete
              options={contractDrafts}
              getOptionLabel={(option) => `${option.number} - ${option.contractor}`}
              onChange={handleDraftSelect}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dodaj na podstawie draftu"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              name="email"
              label="Adres e-mail podwykonawcy"
              value={formData.email}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              name="phone"
              label="Nr telefonu podwykonawcy"
              value={formData.phone}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              name="contract_number"
              label="Numer umowy"
              value={formData.contract_number}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.contract_number}
              helperText={errors.contract_number}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              name="signed_date"
              label="Data podpisania umowy"
              type="date"
              value={formData.signed_date}
              onChange={handleDateChange}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Nazwa podwykonawcy"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="contract_subject"
              label="Przedmiot umowy"
              value={formData.contract_subject}
              onChange={handleChange}
              fullWidth
              required
              multiline
              rows={2}
              error={!!errors.contract_subject}
              helperText={errors.contract_subject}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              name="contract_value"
              label="Wartość umowy"
              type="number"
              value={formData.contract_value}
              onChange={handleNumberChange}
              fullWidth
              required
              InputProps={{
                endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
              }}
              error={!!errors.contract_value}
              helperText={errors.contract_value}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              name="completion_date"
              label="Termin wykonania"
              type="date"
              value={formData.completion_date}
              onChange={handleDateChange}
              fullWidth
              required
              InputLabelProps={{
                shrink: true,
              }}
              error={!!errors.completion_date}
              helperText={errors.completion_date}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box 
              sx={{ 
                border: '1px dashed #ccc', 
                p: 2, 
                borderRadius: 1, 
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
              onClick={() => document.getElementById('contract-file-input')?.click()}
            >
              <input
                id="contract-file-input"
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <CloudUpload sx={{ fontSize: 40, color: '#004273', mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                Dodaj plik umowy
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {contractFile ? `Wybrany plik: ${contractFile.name}` : 'Kliknij, aby wybrać plik (PDF, DOC, DOCX)'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Anuluj
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Dodaj
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSubcontractorForm;
